// The Subcontractor Portal (Session ATA): a subcontractor company's own, read-only view of the jobs ELE has given its
// engineers and of those engineers' recorded hours. Signed in with an emailed 6-digit code (the shared CodeSignIn),
// its own cookie; a 401 from any read sends the person back to sign in.
//
// READ-ONLY FOR JOBS AND HOURS, and the page says so: nothing here changes a booking, accepts a job, edits an hour or
// raises an invoice. Accepting a job is the engineer's own act on their phone (it records that the person going has
// seen the work); a correction is the office's. What the server sends is already an allow-list (no customer, no access
// codes, no description, no price or pay, the address only while work is open), so this page shows what it is given.
//
// THE ONE WRITE (Session AUZ): the Documents tab, where the subcontractor adds or updates a compliance, NVQ Level 3,
// insurance or other document against a named engineer, with a certificate number and an expiry date where one applies,
// from a PDF or a photo in the library (a HEIC photo is converted on the phone by prepareImageForUpload, as the job photo
// upload does). It lands as "submitted, awaiting review": nothing counts until the office approves it, and the page
// says so. No download, no delete here: the file stays with the office and retention stays at Admin. The Your rates tab
// shows the subcontractor's OWN Bona Fide and Labour Only rates side by side and which applies now, nothing else.
//
// THE INVOICES TAB (Session AVB) is a READ: the status of the invoices the subcontractor has emailed to ELE, as the bills
// the office entered in Xero say (received, approved, paid with the date). Reference and total only. When the status
// cannot be read it says "status not available" and never shows an empty list or a guess. Nothing is uploaded here:
// invoices still go by email to the accounts address the page names.
//
// Brand: the Client Portal's canvas and classes (portal-ui), purple #7d21c7 structure, green #63C41F for the one
// positive state, blue #2CB9F3 (the LOLER ink token) for references. No left stripes, no dashes in the copy.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, type SubInvoiceStatusView, type SubPortalDocumentsView, type SubPortalJob, type SubPortalJobsView, type SubPortalRatesView, type SubPortalTimesheetView } from "../../lib/api";
import { CodeSignIn } from "../portal/CodeSignIn";
import { PortalBrandBand, PortalFooter } from "../portal/PortalShell";
import { isHeicFile, prepareImageForUpload } from "../../lib/imageUpload";

const PHONE = "0203 576 4760";

const DATE = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", year: "numeric" });
const TIME = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "long", day: "numeric", month: "short" });

const fmtDate = (iso: string | null) => (iso ? DATE.format(new Date(iso)) : "Date to be confirmed");
const fmtTime = (iso: string | null) => (iso ? TIME.format(new Date(iso)) : null);

export function hoursAndMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// The Monday seven days either side of a YYYY-MM-DD Monday (a date-only value, so UTC arithmetic is exact).
function shiftWeek(weekStarting: string, weeks: number): string {
  const d = new Date(`${weekStarting}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

export function SubcontractorLogin() {
  return (
    <CodeSignIn
      productName="Subcontractor Portal"
      destination="/subcontractor"
      requestCode={(email) => api.subcontractorPortal.requestCode(email)}
      verifyCode={(email, code) => api.subcontractorPortal.verifyCode(email, code)}
      idPrefix="subportal"
    />
  );
}

function JobCard({ job }: { job: SubPortalJob }) {
  const lead = job.engineers.find((e) => e.role === "lead");
  return (
    <li className="rounded-ele border border-ele-purple/40 bg-[rgba(255,255,255,0.04)] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-heading text-lg text-ele-white">
          {job.typeLabel}
          {job.site ? <span className="text-ele-body"> at {job.site.name}</span> : null}
        </p>
        <span className="rounded-full border border-ele-purple px-2 py-0.5 text-xs text-ele-white">{job.statusLabel}</span>
      </div>
      <p className="mt-1 text-sm text-ele-body">{job.open ? fmtDate(job.scheduledFor) : fmtDate(job.completedAt ?? job.scheduledFor)}</p>
      {job.reference ? <p className="mt-1 font-mono text-xs text-ele-loler">{job.reference}</p> : job.eleNumber ? <p className="mt-1 font-mono text-xs text-ele-loler">{job.eleNumber}</p> : null}
      {job.site?.address ? (
        <p className="mt-2 text-sm text-ele-body">
          {job.site.address}
          {job.site.postcode ? `, ${job.site.postcode}` : ""}
        </p>
      ) : null}
      <p className="mt-2 text-sm text-ele-body">
        {job.engineers.map((e) => `${e.name}${e.role === "crew" ? " (second engineer)" : ""}`).join(", ")}
      </p>
      {job.open && lead ? (
        <p className={`mt-1 text-sm ${job.accepted ? "text-ele-green" : "text-ele-body"}`}>
          {job.accepted ? "Accepted by your engineer" : "Not yet accepted by your engineer"}
        </p>
      ) : null}
      {job.arrivedAt || job.leftAt ? (
        <p className="mt-1 text-sm text-ele-body">
          {job.arrivedAt ? `Arrived ${fmtTime(job.arrivedAt)}` : ""}
          {job.arrivedAt && job.leftAt ? " · " : ""}
          {job.leftAt ? `Left ${fmtTime(job.leftAt)}` : ""}
        </p>
      ) : null}
    </li>
  );
}

function JobsTab({ view }: { view: SubPortalJobsView }) {
  if (view.engineers.length === 0) {
    return (
      <p className="rounded-ele border border-ele-purple/40 p-4 text-ele-body">
        No engineers are linked to your company on our system yet, so there is no work to show. Please call the office on {PHONE}.
      </p>
    );
  }
  return (
    <div>
      <h2 className="font-heading text-xl uppercase tracking-wide text-ele-white">Booked and in progress</h2>
      {view.open.length === 0 ? (
        <p className="mt-2 text-ele-body">Nothing is booked for your engineers at the moment.</p>
      ) : (
        <ul className="mt-3 space-y-3">{view.open.map((j) => <JobCard key={j.id} job={j} />)}</ul>
      )}
      <h2 className="mt-8 font-heading text-xl uppercase tracking-wide text-ele-white">Finished in the last {view.recentDays} days</h2>
      {view.recent.length === 0 ? (
        <p className="mt-2 text-ele-body">Nothing finished in this period.</p>
      ) : (
        <ul className="mt-3 space-y-3">{view.recent.map((j) => <JobCard key={j.id} job={j} />)}</ul>
      )}
    </div>
  );
}

// Session AVA: the week's statement of hours, once the office has released it here. Hours only, never money: it supports
// the subcontractor's own invoice and says it is not one. A READ: the download writes nothing.
export function StatementDownload({ statement, engineerName }: { statement: { ref: string; filename: string }; engineerName: string }) {
  const [state, setState] = useState<"idle" | "busy" | "error">("idle");
  async function download() {
    setState("busy");
    try {
      await api.subcontractorPortal.downloadStatement(statement.ref, statement.filename);
      setState("idle");
    } catch {
      setState("error");
    }
  }
  return (
    <div className="mt-2">
      <button
        type="button"
        disabled={state === "busy"}
        onClick={() => void download()}
        className="rounded-ele border border-ele-purple px-3 py-1.5 text-sm text-ele-white disabled:opacity-50"
        aria-label={`Download the statement of hours for ${engineerName}`}
      >
        {state === "busy" ? "Preparing the statement..." : "Statement of hours (PDF)"}
      </button>
      <p className="mt-1 text-xs text-ele-body">Hours only, to support your invoice. It is not an invoice and shows no amounts.</p>
      {state === "error" ? (
        <p role="alert" className="mt-1 text-sm text-ele-white">
          The statement could not be downloaded just now. Try again, or call the office on {PHONE}.
        </p>
      ) : null}
    </div>
  );
}

function HoursTab({ week, onWeek }: { week: SubPortalTimesheetView | null; onWeek: (w: string) => void }) {
  if (!week) return <p className="text-ele-body">Loading hours...</p>;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" className="rounded-ele border border-ele-purple px-3 py-1.5 text-sm text-ele-white" onClick={() => onWeek(shiftWeek(week.weekStarting, -1))}>
          Previous week
        </button>
        <p className="font-heading text-lg text-ele-white">{week.weekLabel}</p>
        <button type="button" className="rounded-ele border border-ele-purple px-3 py-1.5 text-sm text-ele-white" onClick={() => onWeek(shiftWeek(week.weekStarting, 1))}>
          Next week
        </button>
      </div>
      {week.beyondLookback ? (
        <p className="mt-4 text-ele-body">Hours older than 60 days are not shown here. Call the office on 0203 576 4760 if you need them.</p>
      ) : week.engineers.length === 0 ? (
        <p className="mt-4 text-ele-body">No hours are recorded for your engineers this week.</p>
      ) : null}
      {week.engineers.map((e) => (
        <section key={e.engineerName} className="mt-5 rounded-ele border border-ele-purple/40 p-4" aria-label={`Hours for ${e.engineerName}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-heading text-lg text-ele-white">{e.engineerName}</h3>
            <span className={`text-sm ${e.status === "APPROVED" ? "text-ele-green" : "text-ele-body"}`}>{e.statusLabel}</span>
          </div>
          <p className="mt-1 text-sm text-ele-body">
            Total {hoursAndMinutes(e.totals.totalMins)} (on site {hoursAndMinutes(e.totals.onSiteMins)}, travel {hoursAndMinutes(e.totals.travelMins)}
            {e.totals.otherMins ? `, other ${hoursAndMinutes(e.totals.otherMins)}` : ""})
          </p>
          {e.statement ? <StatementDownload statement={e.statement} engineerName={e.engineerName} /> : null}
          {e.visitsAwaitingApproval > 0 ? (
            <p className="mt-1 text-sm text-ele-body">
              {e.visitsAwaitingApproval === 1 ? "1 visit is" : `${e.visitsAwaitingApproval} visits are`} with the office for approval; the hours are added once it is approved.
            </p>
          ) : null}
          <ul className="mt-3 space-y-2">
            {e.days
              .filter((d) => d.entries.length > 0)
              .map((d) => (
                <li key={d.date}>
                  <p className="text-sm font-semibold text-ele-white">{DAY.format(new Date(`${d.date}T00:00:00Z`))}</p>
                  <ul className="mt-1 space-y-1">
                    {d.entries.map((x, i) => (
                      <li key={i} className="text-sm text-ele-body">
                        {x.typeLabel}
                        {x.startTime && x.endTime ? `, ${fmtTime(x.startTime)} to ${fmtTime(x.endTime)}` : ""}
                        {x.minutes != null ? ` (${hoursAndMinutes(x.minutes)})` : ""}
                        {x.siteName ? `, ${x.jobTypeLabel ? `${x.jobTypeLabel} at ` : ""}${x.siteName}` : ""}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ---- Documents (Session AUZ) ----------------------------------------------------------------------------------------
const FIELD = "mt-1 w-full rounded-ele border border-ele-purple/40 bg-[rgba(255,255,255,0.04)] px-3 py-2 text-sm text-ele-white";

const ACCEPTED = "application/pdf,image/*";

export function DocumentsTab({ view, onChanged }: { view: SubPortalDocumentsView; onChanged: () => void }) {
  const [typeKey, setTypeKey] = useState(view.types[0]?.key ?? "");
  const [engineerName, setEngineerName] = useState(view.engineers[0]?.name ?? "");
  const [certificateNumber, setCertificateNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const type = view.types.find((t) => t.key === typeKey) ?? null;

  async function submit() {
    setErr(null);
    setDone(null);
    if (!type) return setErr("Choose the kind of document.");
    if (!file) return setErr("Choose the document to upload: a PDF, or a photo from your library.");
    if (type.personal && !engineerName) return setErr("Choose which engineer this document is for.");
    if (type.expiry === "required" && !expiryDate) return setErr("Enter the expiry date shown on the certificate.");
    setBusy(true);
    try {
      // A photo (HEIC included) is converted and shrunk on the device; a PDF passes through untouched.
      const prepared = file.type.startsWith("image/") || isHeicFile(file) ? await prepareImageForUpload(file) : file;
      const res = await api.subcontractorPortal.uploadDocument(prepared, {
        documentType: type.key,
        engineerName: type.personal ? engineerName : undefined,
        certificateNumber: type.certificateNumber ? certificateNumber || undefined : undefined,
        expiryDate: type.expiry === "none" ? undefined : expiryDate || undefined,
        description: description || undefined,
      });
      setDone(`${res.document.typeLabel}${res.document.engineerName ? ` for ${res.document.engineerName}` : ""} submitted. It is awaiting the office's review and counts once approved.`);
      setFile(null);
      setCertificateNumber("");
      setExpiryDate("");
      setDescription("");
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError && e.message ? e.message : `We could not upload that document. Please try again, or call the office on ${PHONE}.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2 className="font-heading text-xl uppercase tracking-wide text-ele-white">Add or update a document</h2>
      <p className="mt-2 text-sm text-ele-body">
        Compliance certificates, NVQ Level 3, insurance and other documents, for a named engineer where the document is personal. Each upload shows as
        submitted, awaiting review: nothing counts as compliant, or as evidence of insurance, until the office has approved it.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-ele-body">
          Kind of document
          <select className={FIELD} value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
            {view.types.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        {type?.personal && (
          <label className="text-sm text-ele-body">
            Engineer
            <select className={FIELD} value={engineerName} onChange={(e) => setEngineerName(e.target.value)}>
              {view.engineers.length === 0 && <option value="">No engineers are linked to your company yet</option>}
              {view.engineers.map((e) => (
                <option key={e.name} value={e.name}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {type?.certificateNumber && (
          <label className="text-sm text-ele-body">
            Certificate or policy number
            <input type="text" className={FIELD} maxLength={120} value={certificateNumber} onChange={(e) => setCertificateNumber(e.target.value)} />
          </label>
        )}
        {type && type.expiry !== "none" && (
          <label className="text-sm text-ele-body">
            Expiry date{type.expiry === "optional" ? " (if it has one)" : ""}
            <input type="date" className={FIELD} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
          </label>
        )}
        <label className="text-sm text-ele-body sm:col-span-2">
          Description (optional)
          <input type="text" className={FIELD} maxLength={200} value={description} placeholder="For example the insurer or the awarding body" onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="text-sm text-ele-body sm:col-span-2">
          The document: a PDF, or a photo from your library
          <input type="file" accept={ACCEPTED} className={FIELD} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy} onClick={submit} className="rounded-ele bg-ele-purple px-4 py-2 font-menu text-sm uppercase tracking-wide text-white disabled:opacity-60">
          {busy ? "Uploading..." : "Submit for review"}
        </button>
        {done && (
          <p role="status" className="text-sm text-ele-green">
            {done}
          </p>
        )}
      </div>
      {err && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-white">
          {err}
        </p>
      )}

      <h2 className="mt-8 font-heading text-xl uppercase tracking-wide text-ele-white">Documents on file</h2>
      {view.documents.length === 0 ? (
        <p className="mt-2 text-ele-body">No documents are on file yet.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {view.documents.map((d, i) => (
            <li key={i} className="rounded-ele border border-ele-purple/40 bg-[rgba(255,255,255,0.04)] p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-heading text-lg text-ele-white">
                  {d.typeLabel}
                  {d.engineerName ? <span className="text-ele-body"> for {d.engineerName}</span> : null}
                </p>
                <span className={`rounded-full border px-2 py-0.5 text-xs ${d.reviewState === "approved" ? "border-ele-green text-ele-green" : d.reviewState === "rejected" || d.reviewState === "expired" ? "border-ele-danger text-ele-white" : "border-ele-purple text-ele-white"}`}>
                  {d.reviewLabel}
                </span>
              </div>
              <p className="mt-1 text-sm text-ele-body">
                Submitted {fmtDate(d.submittedAt)}
                {d.certificateNumber ? `, number ${d.certificateNumber}` : ""}
                {d.expiryDate ? `, expires ${fmtDate(d.expiryDate)}` : ""}
                {d.description ? `. ${d.description}` : ""}
              </p>
              {d.reviewState === "rejected" && d.reason ? <p className="mt-1 text-sm text-ele-white">Reason: {d.reason}. Please upload a replacement.</p> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- Your rates (Session AUZ) ---------------------------------------------------------------------------------------
export function RatesTab({ rates }: { rates: SubPortalRatesView | null }) {
  if (!rates) return <p className="text-ele-body">Loading your rates...</p>;
  // Session RC1: a supply and fit company has no rate card; one sentence, from the server.
  if (rates.supplyAndFit) {
    return <p className="rounded-ele border border-ele-purple/40 p-4 text-ele-body">{rates.supplyAndFitMessage ?? "You are paid on purchase orders against your own quotes, under your own terms."}</p>;
  }
  if (!rates.present) {
    return <p className="rounded-ele border border-ele-purple/40 p-4 text-ele-body">Your rates are not on our system yet. The office will send them with your welcome pack. Please call {PHONE} with any question.</p>;
  }
  // Session AUZ fix-up: the Labour Only column is shown only when the server says the signed agreement carries it
  // (labourOnlyColumn), and which column applies is the pay engine's own answer for today. A subcontractor on an earlier
  // agreement sees one column and nothing of a Labour Only column or a clause their agreement does not have.
  const twoColumns = rates.labourOnlyColumn;
  return (
    <div>
      <h2 className="font-heading text-xl uppercase tracking-wide text-ele-white">Your rates</h2>
      <p className="mt-2 text-sm text-ele-body">
        {twoColumns ? (
          <>
            In force from {rates.effectiveFromLabel}. The <span className="text-ele-white">{rates.appliesNowLabel}</span> column applies to you at the moment.
            {rates.columnsIdentical ? " The two columns carry the same rates." : ""}
          </>
        ) : (
          <>In force from {rates.effectiveFromLabel}. These are the rates that apply to you.</>
        )}
      </p>
      {rates.columns.map((c) => (
        <section key={c.name} className="mt-4 rounded-ele border border-ele-purple/40 p-4" aria-label={`Rates for ${c.name}`}>
          {rates.columns.length > 1 ? <h3 className="font-heading text-lg text-ele-white">{c.name}</h3> : null}
          <table className="mt-2 w-full text-left text-sm">
            <thead className="font-menu text-xs uppercase tracking-wide text-ele-loler">
              <tr>
                <th className="py-1 pr-2">Rate</th>
                {twoColumns ? (
                  <>
                    <th className={`py-1 pr-2 ${rates.appliesNow === "bona_fide" ? "text-ele-green" : ""}`}>Bona Fide</th>
                    <th className={`py-1 ${rates.appliesNow === "labour_only" ? "text-ele-green" : ""}`}>Labour Only</th>
                  </>
                ) : (
                  <th className="py-1 text-ele-green">Your rate</th>
                )}
              </tr>
            </thead>
            <tbody>
              {c.lines.map((l) => (
                <tr key={l.label} className="border-t border-ele-purple/30">
                  <td className="py-1.5 pr-2 text-ele-body">
                    {l.label} <span className="text-xs">({l.unit})</span>
                  </td>
                  <td className="py-1.5 pr-2 text-ele-white">{l.bonaFide}</td>
                  {twoColumns ? <td className="py-1.5 text-ele-white">{l.labourOnly}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {/* Session RC1: how the card is paid, in plain words, every figure from the card in force (written on the server). */}
      {rates.rules && rates.rules.length > 0 && (
        <section className="mt-4 rounded-ele border border-ele-purple/40 p-4" aria-label="How you are paid">
          <h3 className="font-heading text-lg text-ele-white">How you are paid</h3>
          <ul className="mt-2 space-y-1.5 text-sm text-ele-body">
            {rates.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---- Invoices (Session AVB) -----------------------------------------------------------------------------------------
// The words come from the server (the same object the office reads), so the subcontractor and the office see the same
// thing. The colour only reinforces the word: paid green, the rest in the body and white inks.
const INVOICE_STATE_CLASS: Record<SubInvoiceStatusView["invoices"][number]["state"], string> = {
  received: "text-ele-white",
  approved: "text-ele-loler",
  paid: "text-ele-green",
  not_available: "text-ele-body",
};
const READ_AT = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

export function InvoicesTab({ view }: { view: SubInvoiceStatusView | null }) {
  if (!view) return <p className="text-ele-body">Loading your invoices...</p>;
  return (
    <div>
      <h2 className="font-heading text-xl uppercase tracking-wide text-ele-white">Your invoices</h2>
      <p className="mt-2 text-sm text-ele-body">{view.message}</p>
      {view.invoices.length > 0 ? (
        <table className="mt-4 w-full text-left text-sm">
          <thead className="font-menu text-xs uppercase tracking-wide text-ele-loler">
            <tr>
              <th className="py-1 pr-2">Your reference</th>
              <th className="py-1 pr-2">Total</th>
              <th className="py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {view.invoices.map((r, i) => (
              <tr key={`${r.reference ?? "none"}-${i}`} className="border-t border-ele-purple/30 align-top">
                <td className="py-1.5 pr-2 text-ele-white">{r.reference ?? "No reference recorded"}</td>
                <td className="py-1.5 pr-2 text-ele-white">{r.totalLabel ?? "Not available"}</td>
                <td className="py-1.5">
                  <span className={`font-menu text-xs uppercase tracking-wide ${INVOICE_STATE_CLASS[r.state]}`}>{r.label}</span>
                  <span className="block text-xs text-ele-body">{r.detail}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {view.truncated ? <p className="mt-2 text-xs text-ele-body">Only your 100 most recent invoices are shown.</p> : null}
      {view.readAt ? <p className="mt-3 text-xs text-ele-body">Read from our accounts at {READ_AT.format(new Date(view.readAt))}.</p> : null}
    </div>
  );
}

// Session BAV: shown ONLY to a firm whose engineers work directly for ELE (the server says so from its active linked
// engineers: suppliesEngineers). A firm that only provides services never sees it, and nothing here signs anyone in: no
// username, password or token, only the app's address and how to put it on a phone.
export function EngineerAppCard({ me }: { me: { suppliesEngineers?: boolean; engineerAppUrl?: string; engineers: { name: string; active: boolean }[] } | null }) {
  if (!me || !me.suppliesEngineers || !me.engineerAppUrl) return null;
  const names = me.engineers.filter((e) => e.active).map((e) => e.name);
  return (
    <section aria-label="Engineer app" className="mt-4 rounded-ele border border-ele-purple/40 p-4">
      <h2 className="font-heading text-lg uppercase tracking-wide text-ele-white">Engineer app</h2>
      <p className="mt-2 text-sm text-ele-body">
        Your engineers use our engineer app on their phone to see their jobs and send in their sheets. Open this address on the phone:{" "}
        <a href={me.engineerAppUrl} className="font-semibold underline">{me.engineerAppUrl}</a>
      </p>
      <ul className="mt-2 list-disc pl-5 text-sm text-ele-body">
        <li>iPhone: open it in Safari, tap the Share button, then Add to Home Screen.</li>
        <li>Android: open it in Chrome, tap the menu, then Install app.</li>
      </ul>
      <p className="mt-2 text-sm text-ele-body">Each engineer signs in with their own email address and a 6-digit code the app emails them, so there is no password to lose. If one cannot sign in, please call the office on {PHONE}.</p>
      {names.length > 0 ? <p className="mt-2 text-sm text-ele-body">Set up for: {names.join(", ")}.</p> : null}
    </section>
  );
}

type Tab = "jobs" | "hours" | "documents" | "rates" | "invoices";
const TAB_LABELS: Record<Tab, string> = { jobs: "Jobs", hours: "Hours", documents: "Documents", rates: "Your rates", invoices: "Invoices" };

export function SubcontractorPortal() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("jobs");
  const [jobs, setJobs] = useState<SubPortalJobsView | null>(null);
  const [hours, setHours] = useState<SubPortalTimesheetView | null>(null);
  const [documents, setDocuments] = useState<SubPortalDocumentsView | null>(null);
  const [rates, setRates] = useState<SubPortalRatesView | null>(null);
  const [invoices, setInvoices] = useState<SubInvoiceStatusView | null>(null);
  const [me, setMe] = useState<Awaited<ReturnType<typeof api.subcontractorPortal.me>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The Engineer app card is a nicety: a failed read just leaves it off, never an error on the page.
  useEffect(() => {
    Promise.resolve()
      .then(() => api.subcontractorPortal.me())
      .then(setMe, () => undefined);
  }, []);

  const handle = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.status === 401) navigate("/subcontractor/login", { replace: true });
      else setError(`We could not load this page just now. Please try again, or call the office on ${PHONE}.`);
    },
    [navigate],
  );

  useEffect(() => {
    api.subcontractorPortal.jobs().then(setJobs, handle);
  }, [handle]);

  const loadWeek = useCallback(
    (week?: string) => {
      setHours(null);
      api.subcontractorPortal.timesheet(week).then(setHours, handle);
    },
    [handle],
  );

  const loadDocuments = useCallback(() => {
    api.subcontractorPortal.documents().then(setDocuments, handle);
  }, [handle]);

  // Each tab is loaded the first time it is opened (the hours at the current week, then by the week buttons).
  const [requested, setRequested] = useState<Partial<Record<Tab, boolean>>>({});
  function openTab(t: Tab) {
    setTab(t);
    if (requested[t]) return;
    setRequested((r) => ({ ...r, [t]: true }));
    if (t === "hours") loadWeek();
    else if (t === "documents") loadDocuments();
    else if (t === "rates") api.subcontractorPortal.rates().then(setRates, handle);
    else if (t === "invoices") api.subcontractorPortal.invoices().then(setInvoices, handle);
  }

  async function signOut() {
    try {
      await api.subcontractorPortal.logout();
    } finally {
      navigate("/subcontractor/login", { replace: true });
    }
  }

  return (
    <div className="portal-ui portal-light flex min-h-dvh flex-col bg-white text-ele-body">
      <div aria-hidden className="ele-portal-accent" />
      <PortalBrandBand onSignOut={signOut} label="Subcontractor Portal" home="/subcontractor" signOutAlways />
      <div className="mx-auto w-full max-w-3xl flex-1 px-4 py-6">
        {jobs ? <p className="font-heading text-lg uppercase tracking-wide text-ele-white">{jobs.company}</p> : null}

        <p className="mt-4 text-sm text-ele-body">
          This page shows the work we have booked for your engineers, the hours recorded against it and your rates, all for viewing only: to change a booking
          or correct an hour, please call the office on {PHONE}. Under Documents you can add or update your compliance, NVQ and insurance documents. Under
          Invoices you can see where each invoice you have sent us has got to.
        </p>

        <EngineerAppCard me={me} />

        <nav className="mt-5 flex flex-wrap gap-2" aria-label="Sections">
          {(["jobs", "hours", "documents", "rates", "invoices"] as const).map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={tab === t}
              onClick={() => openTab(t)}
              className={`rounded-ele px-4 py-2 font-menu text-sm uppercase tracking-wide ${tab === t ? "bg-ele-purple text-white" : "border border-ele-purple text-ele-white"}`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </nav>

        <main className="mt-6">
          {error ? (
            <p role="alert" className="rounded-ele border border-ele-danger px-3 py-2 text-ele-white">
              {error}
            </p>
          ) : tab === "jobs" ? (
            jobs ? <JobsTab view={jobs} /> : <p className="text-ele-body">Loading jobs...</p>
          ) : tab === "hours" ? (
            <HoursTab week={hours} onWeek={loadWeek} />
          ) : tab === "documents" ? (
            documents ? <DocumentsTab view={documents} onChanged={loadDocuments} /> : <p className="text-ele-body">Loading documents...</p>
          ) : tab === "rates" ? (
            <RatesTab rates={rates} />
          ) : (
            <InvoicesTab view={invoices} />
          )}
        </main>
        <PortalFooter light />
      </div>
    </div>
  );
}
